class QuickViewDrawer extends HTMLElement {
  connectedCallback() {
    this._inner = this.querySelector('.qvd-inner');
    this._content = this.querySelector('.qvd-content');
    this._closeBtn = this.querySelector('.qvd-close');

    // Close when clicking the backdrop (anywhere outside the inner panel).
    // composedPath() captures the path at dispatch time — safe even if the target
    // is removed from the DOM before the event bubbles here (e.g. swatch click triggers
    // _showLoading which clears innerHTML before bubbling reaches this listener).
    this.addEventListener('click', (e) => {
      if (this._inner && e.composedPath().includes(this._inner)) return;
      this.close();
    });

    this._closeBtn?.addEventListener('click', () => this.close());

    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.classList.contains('active')) this.close();
    });

    // Close quick-view when cart drawer opens (e.g. after Add to Cart)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(() => {
        if (cartDrawer.classList.contains('active') && this.classList.contains('active')) {
          this.close();
        }
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  open(productUrl, openerBtn) {
    this._openerBtn = openerBtn || null;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      cartDrawer.close();
    }

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    this._showLoading();
    this._fetchSection(productUrl);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    // slider-container's disconnectedCallback auto-destroys Swiper when innerHTML is cleared
    this._content.innerHTML = '';

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      document.body.classList.add('overflow-hidden');
    }
  }

  _showLoading() {
    this._content.innerHTML =
      '<div class="qvd-loading"><div class="loading__spinner"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div>';
  }

  async _fetchSection(productUrl) {
    try {
      const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}sections=quick-view-drawer`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const html = data['quick-view-drawer'];
      if (!html) throw new Error('Section not found in response');

      this._content.innerHTML = html;

      if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();

      this._initVariantHandler();
      this._initSwatches(productUrl);
    } catch (err) {
      this._content.innerHTML =
        '<div class="qvd-loading" style="flex-direction:column;gap:1rem;"><p>Could not load product. Please try again.</p></div>';
      console.error('[QuickViewDrawer] fetch error:', err);
    }
  }

  _initVariantHandler() {
    const variantSelects = this._content.querySelector('variant-selects');
    if (!variantSelects) return;

    const dataEl = this._content.querySelector('[data-qvd-product-data]');
    if (!dataEl) return;

    let productData;
    try {
      productData = JSON.parse(dataEl.textContent);
    } catch (_) {
      return;
    }
    const { variants } = productData;

    variantSelects.addEventListener('change', () => {
      const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
      const selectedValues = fieldsets.map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });

      const variant = variants.find((v) =>
        selectedValues.every((val, idx) => val === null || v.options[idx] === val)
      );

      if (variant) this._applyVariant(variant);
    });
  }

  _applyVariant(variant) {
    const variantInput = this._content.querySelector('.product-variant-id');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    const atcBtn = this._content.querySelector('.qvd-atc-btn');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      const span = atcBtn.querySelector('span:not(.loading__spinner *)');
      if (span) {
        span.textContent = variant.available
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    const priceContainer = this._content.querySelector('.qvd-price');
    if (priceContainer) {
      const onSale = variant.compare_at_price_html !== null;
      const priceEl = priceContainer.querySelector('.price');
      if (priceEl) {
        priceEl.classList.toggle('price--on-sale', onSale);
        priceEl.classList.toggle('price--sold-out', !variant.available);
      }
      const regularEl = priceContainer.querySelector('.price__regular .price-item--regular');
      if (regularEl) regularEl.innerHTML = variant.price_html || '';

      const salePriceEl = priceContainer.querySelector('.price-item--sale');
      if (salePriceEl) salePriceEl.innerHTML = variant.price_html || '';

      const compareEl = priceContainer.querySelector('.price__sale s .price-item--regular, .price__sale s');
      if (compareEl) compareEl.innerHTML = variant.compare_at_price_html || '';
    }

    if (variant.featured_media_id) {
      const sliderEl = this._content.querySelector('slider-container');
      const swiper = sliderEl?.swiperInstance;
      if (swiper) {
        const slides = Array.from(this._content.querySelectorAll('.swiper-slide'));
        const idx = slides.findIndex((s) => String(s.dataset.mediaId) === String(variant.featured_media_id));
        if (idx >= 0) swiper.slideTo(idx, 300);
      }
    }
  }

  _initSwatches(currentProductUrl) {
    const swatchBtns = this._content.querySelectorAll('[data-qvd-swatch]');
    swatchBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.productUrl;
        if (!url || url === currentProductUrl) return;

        // _showLoading clears innerHTML → slider-container disconnects → auto-destroys swiper
        this._showLoading();
        this._fetchSection(url);
      });
    });
  }
}

if (!customElements.get('quick-view-drawer')) {
  customElements.define('quick-view-drawer', QuickViewDrawer);
}

// Global delegate — any element with [data-quick-view-btn] and [data-product-url] opens the drawer
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-view-btn]');
  if (!btn) return;
  const productUrl = btn.dataset.productUrl;
  if (!productUrl) return;
  const drawer = document.querySelector('quick-view-drawer');
  if (!drawer) return;

  e.preventDefault();
  drawer.open(productUrl, btn);
});
